import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, interval, of, startWith, switchMap } from 'rxjs';
import { BrandVoucherDetail } from '@/app/common/general-types/brand-voucher.dto';
import { VoucherService } from '@/app/services/voucher/voucher.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { ThemeService } from '@/app/services/theme/theme.service';
import { EchartGenericChartComponent } from '@/app/charts/echart-generic-chart/echart-generic-chart.component';
import { chartVariantForMetricKey } from '../../../admin-stats-dashboard/admin-stats-dashboard.helpers';
import {
  buildVoucherUsageChart,
  buildVoucherUsagePoints,
  voucherExpiryFillClass,
  voucherTimeLeftLabel,
  voucherTimeRemainingPercent,
  type VoucherRightPanelTab,
} from './brand-voucher-profile.helpers';

@Component({
  selector: 'admin-tool-brand-voucher-profile',
  imports: [DatePipe, EchartGenericChartComponent],
  templateUrl: './brand-voucher-profile.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBrandVoucherProfile implements OnInit {
  private readonly voucherService = inject(VoucherService);
  private readonly toast = inject(ToastNotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly themeService = inject(ThemeService);

  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly voucher = signal<BrandVoucherDetail | null>(null);
  readonly nowMs = signal(Date.now());
  readonly rightPanelTab = signal<VoucherRightPanelTab>('redeemed');

  readonly redemptions = computed(() => {
    const list = this.voucher()?.redemptions ?? [];
    return [...list].sort(
      (a, b) => new Date(b.redeemedAt).getTime() - new Date(a.redeemedAt).getTime(),
    );
  });

  readonly timeRemainingPercent = computed(() => {
    const voucher = this.voucher();
    if (!voucher) {
      return null;
    }
    return voucherTimeRemainingPercent(voucher.createdDate, voucher.expiresAt, this.nowMs());
  });

  readonly expiryBarWidth = computed(() => {
    const remaining = this.timeRemainingPercent();
    if (remaining == null) {
      return 0;
    }
    return remaining <= 0 ? 100 : remaining;
  });

  readonly expiryFillClass = computed(() => voucherExpiryFillClass(this.timeRemainingPercent()));

  readonly timeLeftLabel = computed(() => {
    const voucher = this.voucher();
    if (!voucher) {
      return '';
    }
    return voucherTimeLeftLabel(voucher.expiresAt, this.nowMs());
  });

  readonly usageChart = computed(() => {
    const voucher = this.voucher();
    const points = buildVoucherUsagePoints(
      voucher?.createdDate,
      voucher?.expiresAt,
      voucher?.redemptions ?? [],
    );
    const built = buildVoucherUsageChart(points);
    return {
      ...built,
      chartVariant: chartVariantForMetricKey('searches', this.themeService.isDarkMode()),
    };
  });

  selectRightPanelTab(tab: VoucherRightPanelTab): void {
    this.rightPanelTab.set(tab);
  }

  rightPanelTabClass(tab: VoucherRightPanelTab): string {
    if (this.rightPanelTab() === tab) {
      return 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200';
    }
    return 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  constructor() {
    interval(60000)
      .pipe(startWith(0), takeUntilDestroyed())
      .subscribe(() => this.nowMs.set(Date.now()));
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const id = params.get('id');
          if (!id) {
            this.loadingState.set('error');
            this.voucher.set(null);
            return of(null);
          }
          this.loadingState.set('loading');
          return this.voucherService.getById(id).pipe(
            catchError(() => {
              this.loadingState.set('error');
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((voucher) => {
        this.voucher.set(voucher);
        if (voucher) {
          this.loadingState.set('loaded');
        } else if (this.loadingState() !== 'error') {
          this.loadingState.set('error');
        }
        this.cdr.detectChanges();
      });
  }

  async copyName(name: string): Promise<void> {
    const value = name?.trim();
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      await this.toast.alert({
        title: 'Copied',
        message: 'Copied',
        type: 'success',
        icon: 'success',
      });
    } catch {
      await this.toast.alert({
        title: 'Copy failed',
        message: 'Could not copy the voucher name.',
        type: 'error',
        icon: 'error',
      });
    }
  }
}
